import { useEffect, useState } from 'react';
import { useSharedFamilyComposition } from '../../../hooks/useSharedFamilyComposition';
import { findSharedFamilyComposition } from '../../../utils/familyComposition';

// Seeds this screen's address (BA/VP) and Gezinssamenstelling from the canonical shared
// address and the upstream products (Personenauto/Woonhuis/Inboedel/AVP), so the calc can
// run even when those were filled on an earlier screen. Returns whether GSS is locked
// (i.e. supplied upstream → read-only here). Extracted verbatim from RechtsbijstandScreen;
// the effect dependency arrays are kept identical to preserve the original timing.
interface UpstreamSyncArgs {
  data: any;
  sharedAddress: any;
  selectedProducts: any;
  getStepData: (id: string) => any;
  persist: (updater: (prev: any) => any) => void;
}

export function useUpstreamSync({
  data,
  sharedAddress,
  selectedProducts,
  getStepData,
  persist,
}: UpstreamSyncArgs) {
  const [isGezinsamenstellingLocked, setIsGezinsamenstellingLocked] = useState(false);

  // Initialize / refresh address fields from sharedAddress (the canonical policyholder
  // address). sharedAddress wins over local data so an edit made on another product
  // while RBV was deactivated still flows in here when the user returns. Falls back
  // to scanning other products' step data only when sharedAddress hasn't been seeded.
  useEffect(() => {
    const ba = data?.Contractdocument?.PP?.BA;
    const vp = data?.Contractdocument?.PP?.VP;

    const upstreamBA =
      (selectedProducts?.personenauto && getStepData('personenauto')?.Contractdocument?.PP?.BA) ||
      (selectedProducts?.woonhuis && getStepData('woonhuis')?.Contractdocument?.PP?.BA) ||
      (selectedProducts?.inboedel && getStepData('inboedel')?.Contractdocument?.PP?.BA) ||
      (selectedProducts?.aansprakelijkheid && getStepData('aansprakelijkheid')?.Contractdocument?.PP?.BA) ||
      null;

    // sharedAddress > local data > upstream — flipped so the canonical source wins.
    const bestPcode = sharedAddress?.BA_PCODE || ba?.BA_PCODE || vp?.VP_PCODE || upstreamBA?.BA_PCODE;
    const bestHuisnr = sharedAddress?.BA_HUISNR || ba?.BA_HUISNR || vp?.VP_HUISNR || upstreamBA?.BA_HUISNR;
    const bestToevoeg = sharedAddress?.BA_TOEVOEG ?? ba?.BA_TOEVOEG ?? vp?.VP_TOEVOEG ?? upstreamBA?.BA_TOEVOEG ?? '';
    const bestStraat = sharedAddress?.BA_STRAAT || ba?.BA_STRAAT || vp?.VP_STRAAT || upstreamBA?.BA_STRAAT;
    const bestPlaats = sharedAddress?.BA_PLAATS || ba?.BA_PLAATS || vp?.VP_PLAATS || upstreamBA?.BA_PLAATS;

    // Seed data.BA/VP from the canonical address so the calc can run even when the
    // address was supplied upstream (AddressSection isn't rendered in that case).
    // AddressSection owns the editable inputs + lookup when it IS shown.
    if (bestStraat && bestPlaats) {
      persist((prev: any) => {
        const prevBA = prev.Contractdocument?.PP?.BA || {};
        const prevVP = prev.Contractdocument?.PP?.VP || {};
        return {
          ...prev,
          Contractdocument: {
            ...prev.Contractdocument,
            PP: {
              ...prev.Contractdocument?.PP,
              BA: {
                ...prevBA,
                BA_PCODE: bestPcode,
                BA_HUISNR: bestHuisnr,
                BA_TOEVOEG: bestToevoeg,
                BA_STRAAT: bestStraat,
                BA_PLAATS: bestPlaats,
              },
              VP: {
                ...prevVP,
                VP_PCODE: bestPcode || prevVP.VP_PCODE,
                VP_HUISNR: (bestHuisnr ? Number(bestHuisnr) : undefined) || prevVP.VP_HUISNR || 0,
                VP_TOEVOEG: bestToevoeg || prevVP.VP_TOEVOEG || '',
                VP_STRAAT: bestStraat || prevVP.VP_STRAAT,
                VP_PLAATS: bestPlaats || prevVP.VP_PLAATS,
              },
            },
          },
        };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sharedAddress is the trigger; the effect writes data.BA/VP itself
  }, [sharedAddress]);

  // Single source of truth for the family composition across every product.
  const { familyComposition, setFamilyComposition } = useSharedFamilyComposition();

  // Gezinssamenstelling: a selected upstream product (Woonhuis/Inboedel/AVP) OWNS it -> lock and
  // reuse (hidden, not re-asked). The value itself lives in the single shared store; seed it from
  // an upstream owner or any value the user already picked (incl. on a since-deselected product).
  useEffect(() => {
    const woonhuisGSS = selectedProducts?.woonhuis ? getStepData('woonhuis')?.Contractdocument?.PP?.OP?.OP_GSS : null;
    const inboedelGSS = selectedProducts?.inboedel ? getStepData('inboedel')?.Contractdocument?.PP?.IP?.IP_GSS : null;
    const avpGSS = selectedProducts?.aansprakelijkheid ? getStepData('aansprakelijkheid')?.Contractdocument?.PP?.DA?.DA_GSS : null;
    const upstreamGSS = woonhuisGSS || inboedelGSS || avpGSS;
    setIsGezinsamenstellingLocked(!!upstreamGSS);
    if (!familyComposition) {
      const found = upstreamGSS || findSharedFamilyComposition(getStepData);
      if (found) setFamilyComposition(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  // Mirror the shared value into VK_GEZSAMST so the BFF payload carries it.
  useEffect(() => {
    if (!familyComposition) return;
    persist((prev: any) => {
      if (prev?.Contractdocument?.PP?.VK?.VK_GEZSAMST === familyComposition) return prev;
      return {
        ...prev,
        Contractdocument: {
          ...prev.Contractdocument,
          PP: {
            ...prev.Contractdocument?.PP,
            VK: { ...prev.Contractdocument?.PP?.VK, VK_GEZSAMST: familyComposition },
          },
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  return { isGezinsamenstellingLocked, familyComposition, setFamilyComposition };
}
